import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { NAV, LOGO_URL } from "../data/content";
import { OrangeButton } from "./blocks";
import { useBooking } from "../context/BookingContext";
import { OptImg } from "@/components/OptImg";

const mainLinks = [
  { label: "Home", to: "/" },
  { label: "IBN Network", to: "/founder-network" },
];

function Dropdown({ label, items, onNavigate }) {
  return (
    <div className="relative group">
      <button className="flex items-center gap-1 text-white hover:text-embersoft text-[15px] py-2 transition-colors">
        {label}
        <ChevronDown className="w-4 h-4 transition-transform group-hover:rotate-180" />
      </button>
      <div className="absolute left-0 top-full pt-3 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200 z-50">
        <div className="bg-surface rounded-2xl shadow-[0_24px_60px_rgba(0,0,0,0.4)] py-3 min-w-[230px]">
          {items.map((it) => {
            const cls = "block px-6 py-2.5 text-white hover:text-embersoft hover:bg-callout/50 text-[15px] transition-colors";
            return it.external ? (
              <a key={it.label} href={it.to} target="_blank" rel="noreferrer" onClick={onNavigate} className={cls} data-testid={`nav-drop-${it.label.toLowerCase().replace(/\s+/g, "-")}`}>
                {it.label}
              </a>
            ) : (
              <Link key={it.label} to={it.to} onClick={onNavigate} className={cls} data-testid={`nav-drop-${it.label.toLowerCase().replace(/\s+/g, "-")}`}>
                {it.label}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { openBooking } = useBooking();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMobileOpen(false), [location.pathname]);

  return (
    <nav className={`fixed top-0 inset-x-0 ${mobileOpen ? "z-[130]" : "z-[60]"} transition-colors duration-300 ${scrolled ? "bg-ink/95 backdrop-blur-md" : "bg-transparent"}`}>
      <div className="max-w-7xl mx-auto px-6 sm:px-10 flex items-center justify-between h-16 mt-2">
        <Link to="/" className="leading-none" data-testid="nav-logo">
          <OptImg sizes="260px" loading="eager" src={LOGO_URL} alt="The Intuitive Leader" className="h-8 sm:h-9 w-auto" />
        </Link>

        <div className="hidden lg:flex items-center gap-8">
          {mainLinks.map((l) => (
            <Link key={l.to} to={l.to} className="text-white hover:text-embersoft text-[15px] transition-colors">
              {l.label}
            </Link>
          ))}
          <Dropdown label="Services" items={NAV.services} />
          <Dropdown label="Insights" items={NAV.insights} />
          <Link to="/about-maurice-jenkens" className="text-white hover:text-embersoft text-[15px] transition-colors">About</Link>
          <button
            onClick={() => openBooking("Contact")}
            data-testid="nav-contact-btn"
            className="text-[15px] text-white hover:text-embersoft transition-colors"
          >
            Contact
          </button>
          <span className="h-5 w-px bg-white/25" aria-hidden="true" />
          <a
            href="https://login.circle.so/sign_in?request_host=intuition-business-network.circle.so"
            target="_blank"
            rel="noreferrer"
            className="text-[15px] text-white hover:text-embersoft transition-colors"
            data-testid="nav-community"
          >
            Community
          </a>
        </div>

        <button className="lg:hidden text-cream" onClick={() => setMobileOpen(true)} data-testid="nav-mobile-open" aria-label="Open menu">
          <Menu className="w-7 h-7" />
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[70] bg-ink lg:hidden overflow-y-auto">
          <div className="flex justify-between items-center px-6 h-16">
            <span className="leading-none"><OptImg sizes="260px" loading="eager" src={LOGO_URL} alt="The Intuitive Leader" className="h-8 w-auto" /></span>
            <button onClick={() => setMobileOpen(false)} data-testid="nav-mobile-close" aria-label="Close menu">
              <X className="w-7 h-7 text-cream" />
            </button>
          </div>
          <div className="px-6 py-6 flex flex-col gap-1">
            {mainLinks.map((l) => (
              <Link key={l.to} to={l.to} className="font-heading text-2xl text-cream py-3">{l.label}</Link>
            ))}
            <p className="font-heading text-2xl text-cream pt-3 pb-1 mt-4" data-testid="nav-mobile-services-heading">Services</p>
            {NAV.services.map((it) => (
              <Link key={it.label} to={it.to} className="text-white/70 py-2 pl-4 text-lg">{it.label}</Link>
            ))}
            <p className="font-heading text-2xl text-cream pt-3 pb-1 mt-4" data-testid="nav-mobile-insights-heading">Insights</p>
            {NAV.insights.map((it) => (
              it.external ? (
                <a key={it.label} href={it.to} target="_blank" rel="noreferrer" className="text-cream/70 py-2 pl-4 text-lg">{it.label}</a>
              ) : (
                <Link key={it.label} to={it.to} className="text-cream/70 py-2 pl-4 text-lg">{it.label}</Link>
              )
            ))}
            <Link to="/about-maurice-jenkens" className="font-heading text-2xl text-cream py-3 mt-4">About</Link>
            <a href="https://login.circle.so/sign_in?request_host=intuition-business-network.circle.so" target="_blank" rel="noreferrer" className="font-heading text-2xl text-cream py-3" data-testid="nav-community-mobile">Community</a>
            <OrangeButton onClick={() => { setMobileOpen(false); navigate("/"); openBooking("Contact"); }} className="mt-6 w-full">
              CONTACT ME
            </OrangeButton>
          </div>
        </div>
      )}
    </nav>
  );
}
